import { DateTime, Interval } from "luxon";
import { CalendarDaysIcon } from "@heroicons/react/24/outline/index.js";
import {
  cn,
  DrawerOrSheet,
  getTailwindClassNamesFromHex,
  useIsGummy
} from "@swivu/shared-ui";
import { Link, useParams } from "react-router-dom";
import { Button } from "@swivu/shared-ui/src/components/Button.js";
import { useTeamSchedule } from "./TeamScheduleContext.js";
import { GridData } from "./groupTeamSchedule.js";
import { ArrowRightIcon } from "@heroicons/react/24/solid/index.js";
import { isHexColorLightOrDark } from "./isHexColorLightOrDark.js";

type ITasks = GridData[number]["dates"];

type ThisDaysTasks = GridData[number] & { tasks: ITasks };

function TeamScheduleDay({ day }: { day: Date }) {
  const { timezone } = useTeamSchedule();
  const isGummy = useIsGummy();
  useParams<{
    taskId?: string;
    slug: string;
  }>();

  const luxonDay = DateTime.fromJSDate(day)
    .setZone(timezone)
    .plus({ seconds: 1 });
  const dayString = luxonDay.toFormat("EEE dd");
  const dayWeekday = luxonDay.toFormat("EEE");
  const dayNum = luxonDay.toFormat("dd");
  const dayStringDetail = luxonDay.toFormat("EEEE MMMM dd, yyyy");

  const { groupedByTaskData, slideoutContainerId } = useTeamSchedule();
  const filteredTeamScheduleDay =
    groupedByTaskData?.filter((item) => item?.dates?.length) ?? [];

  const dayStart = DateTime.fromJSDate(day).setZone(timezone).startOf("day");
  const dayEnd = DateTime.fromJSDate(day).setZone(timezone).endOf("day");

  // in case you want to link directly
  // const links = generateLink({
  //   dayEnd,
  //   dayStart,
  //   timezone
  // });

  const thisDaysTasks = filteredTeamScheduleDay
    .reduce<ThisDaysTasks[]>((acc, curr) => {
      const tasks: ITasks = [];
      curr.dates.forEach((task) => {
        const start = DateTime.fromISO(task.start_time).setZone(timezone);
        const end = DateTime.fromISO(task.end_time)
          .setZone(timezone)
          .minus({ seconds: 1 });

        const taskInterval = Interval.fromDateTimes(start, end);
        const dayStartInTask = taskInterval.contains(dayStart);
        const dayEndInTask = taskInterval.contains(dayEnd);
        const taskInDay = Interval.fromDateTimes(dayStart, dayEnd).overlaps(
          taskInterval
        );

        if (dayStartInTask || dayEndInTask || taskInDay) {
          tasks.push(task);
        }
      });
      if (tasks.length) {
        acc.push({
          ...curr,
          tasks
        });
      }
      return acc;
    }, [])
    .sort((a, b) => {
      return a.rowLabel.localeCompare(b.rowLabel);
    });
  const isToday = luxonDay
    .startOf("day")
    .hasSame(DateTime.now().setZone(timezone).startOf("day"), "day");
  return (
    <DrawerOrSheet.Root key={day.toISOString()} dialog={{}} drawer={{}}>
      <>
        <DrawerOrSheet.Trigger>
          {day &&
            (isGummy ? (
              isToday ? (
                <span
                  role="button"
                  className={cn(
                    "inline-flex h-11 items-center rounded-full px-4 text-sm font-display font-black uppercase tracking-widest text-white",
                    "bg-tile-calendar",
                    "transition-transform duration-150 gummy-lift active:translate-y-0 active:scale-[0.96]",
                    "shadow-pill",
                    "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/80 motion-reduce:transform-none"
                  )}
                  style={{ textShadow: "var(--text-shadow-on-color)" }}
                >
                  Today
                </span>
              ) : (
                <span
                  role="button"
                  className={cn(
                    "inline-flex flex-col items-center justify-center min-h-11 rounded-surface-sm px-2 py-1.5 text-base font-display font-black tracking-tight text-mono-700 dark:text-mono-300 leading-tight",
                    "transition-transform duration-150 gummy-lift active:translate-y-0 active:scale-[0.96] motion-reduce:transform-none",
                    "hover:bg-mono-200/60 dark:hover:bg-mono-800/60"
                  )}
                >
                  <span>{dayWeekday}</span>
                  <span>{dayNum}</span>
                </span>
              )
            ) : (
              <Button
                role={"span"}
                action={`secondary`}
                className={
                  "border bg-white hover:bg-mono-50 px-2 py-1 text-xs dark:border-opacity-50 sm:px-4 sm:py-1.5 "
                }
              >
                {isToday ? "Today" : dayString}
              </Button>
            ))}
        </DrawerOrSheet.Trigger>
        <DrawerOrSheet.Content
          slideoutContainerId={slideoutContainerId}
          className="flex  bg-mono-100 flex-col gap-0 sm:max-w-xl  dark:bg-mono-950 dark:text-mono-200 px-4 pb-6 mx-auto"
        >
          <DrawerOrSheet.Header
            drawer={{
              className: `border-b dark:border-mono-700/50 py-4 pb-0 px-0`
            }}
          >
            <DrawerOrSheet.Title className={`px-0`}>
              <div
                className={cn(
                  "flex items-center gap-3",
                  isGummy && "text-lg font-display font-black"
                )}
              >
                <CalendarDaysIcon className="size-7" />
                <div>{dayStringDetail}</div>
              </div>
            </DrawerOrSheet.Title>
          </DrawerOrSheet.Header>
          <div className="flex-1 overflow-scroll pt-6">
            <div className=" h-full  space-y-5 ">
              {thisDaysTasks?.length === 0 && (
                <div
                  className={cn(
                    "text-mono-500 dark:text-mono-400",
                    isGummy ? "text-base font-display font-black" : "text-sm"
                  )}
                >
                  No tasks scheduled for this day
                </div>
              )}
              {thisDaysTasks.map((task, i) => {
                return (
                  <div key={`${i}${task.rowLabel}`}>
                    <Link to={`${task.rowLink}`}>
                      <div
                        className={cn(
                          isGummy &&
                            "text-base font-display font-black tracking-tight text-mono-800 dark:text-mono-100 mb-1"
                        )}
                      >
                        {task.rowLabel}
                      </div>
                    </Link>
                    {/*<AccordionV2.Root className={`space-y-2`} type="multiple">*/}
                    {task.tasks.map((t, i) => {
                      return <DayItem t={t} key={t.resourceId + i} />;
                    })}
                    {/*</AccordionV2.Root>*/}
                  </div>
                );
              })}
            </div>
          </div>
        </DrawerOrSheet.Content>
      </>
    </DrawerOrSheet.Root>
  );
}

const DayItem = ({ t }: { t: ThisDaysTasks["tasks"][number] }) => {
  const isGummy = useIsGummy();
  return (
    <Link to={`${t.dateLink}`} className={``} viewTransition={true}>
      <div
        style={
          isGummy
            ? {
                backgroundColor: t.color,
                backgroundImage:
                  "linear-gradient(to bottom, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 60%, rgba(0,0,0,0.1) 100%)",
                color: "#fff",
                textShadow: "var(--text-shadow-on-color)"
              }
            : { background: t.color, color: isHexColorLightOrDark(t.color) }
        }
        className={cn(
          "block flex mt-1",
          isGummy
            ? cn(
                "rounded-surface-md overflow-hidden",
                "shadow-raised",
                "transition-transform duration-150 gummy-lift active:translate-y-0 active:scale-[0.97] motion-reduce:transform-none"
              )
            : `border-none rounded-md py-0 ${getTailwindClassNamesFromHex(t.color)}`
        )}
      >
        <div
          key={`${t.resourceId}`}
          className={cn(
            "flex-1 text-left group flex justify-between items-center",
            isGummy
              ? "px-4 py-3 text-base font-display font-black tracking-tight"
              : "px-2 py-1"
          )}
        >
          <div
            key={`${t.resourceId}`}
            style={{
              ...(t.viewTransitions?.title && {
                viewTransitionName: t.viewTransitions.title
              })
            }}
          >
            {t.label}
          </div>
          <ArrowRightIcon
            className={cn(
              "chevron shrink-0 sm:opacity-0 group-hover:opacity-100 sm:-translate-x-5 group-hover:translate-x-0 transition-all duration-200",
              isGummy ? "size-5" : "size-4"
            )}
          />
        </div>
      </div>
    </Link>
  );
};

export default TeamScheduleDay;
